/* eslint-disable camelcase */
// Modules
import { decodeClid } from './utils/decodeClid.js';
import { cutParamFromUrl } from './utils/cutParamFromUrl.js';
import { sendHit } from './utils/sendHit.js';
import { getSidCookieOptions } from './utils/cookieUtils.js';

// Utils
import { debug } from './utils/debug.js';
import { COOKIE, URL } from '@iva/utils';

// Constants
import { ACTIONS, EVENTS, COOKIE_NAMES, PARAM_NAMES, SID_BLACKLIST } from '../config.js';

export const handleMissingSid = (sid) => {
  debug(`did not find SID cookie, creating new`);
  COOKIE.setCookie(COOKIE_NAMES.SID, sid, getSidCookieOptions());
  if (window.sznIVA && window.sznIVA.IS) {
    window.sznIVA.IS.updateIdentities({ sid });
  }
  window.dispatchEvent(new CustomEvent(EVENTS.SID_STORED, { detail: sid }));
};

/**
 * Matches SID with cookie and sends hit with action and returns action type as a string.
 * @param {String} sid - User's ID.
 * @return {('notstored' | 'same' | 'different')} Action based on context in browser.
 */
export const storeSid = (sid, reason) => {
  // get actual sid
  const localSidCookie = COOKIE.getCookie(COOKIE_NAMES.SID);

  if (!localSidCookie) {
    // not stored sid, linkdecoration saves cookie
    sendHit(ACTIONS.SID.NOT_STORED, { reason, ld_sid: sid, dsid: null }, true);
    handleMissingSid(sid);
    return ACTIONS.SID.NOT_STORED;
  }
  if (localSidCookie === sid) {
    // both are same
    debug('found SID cookie with the same SID value');
    sendHit(ACTIONS.SID.SAME, { reason, ld_sid: sid, dsid: localSidCookie }, true);
    return ACTIONS.SID.SAME;
  }
  debug('found SID cookie with different value from linkdec SID');
  sendHit(ACTIONS.SID.DIFFERENT, { reason, ld_sid: sid, dsid: localSidCookie }, true);
  return ACTIONS.SID.DIFFERENT;
};

/**
 * Attempts to retrieve SID from UTM.
 * @return {String | null} SID or null if fail to parse or no sznclid in URL.
 */
export const getSid = () => {
  debug({
    message: 'getting SID',
    state: 'running',
    sid: {
      state: 'start',
    },
  });

  const sznclid = cutParamFromUrl(window.location.href, PARAM_NAMES.SZNCLID);

  // blacklist
  const domain = URL.getDomainLevel(undefined, undefined, false);
  if (SID_BLACKLIST.includes(domain)) {
    debug(`SID handler stoped, domain "${domain}" is blacklisted.`);
    return null;
  }

  if (!sznclid) {
    debug({
      state: 'running',
      message: 'did not find sznclid in url',
      sid: {
        state: 'done',
        error: 'no sznclid in URL',
        sznclid,
      },
    });
    return null;
  }

  debug(`found parameter "${PARAM_NAMES.SZNCLID}", decoding...`);
  const sid = decodeClid(sznclid);
  if (sid) {
    debug({
      state: 'running',
      message: 'successfully retrieved "sid"',
      sid: {
        state: 'done',
        sid,
      },
    });
    return sid;
  }

  debug({
    state: 'running',
    message: 'sznclid is invalid - could not be decoded',
    sid: {
      state: 'error',
      sid,
      sznclid,
      error: 'failed to decode sznclid',
    },
  });
  return null;
};

/**
 * Validates SID.
 * @param {String} sid - SID to be validated.
 * @return {Boolean} True if valid, false otherwise.
 */
export const isValid = (sid) => {
  // SID format described here:
  // https://gitlab.seznam.net/datacollect/datacollect-nginx-modules/-/tree/master/ap_mod#sid-cookie-format
  const regex = /^id=[0-9]+\|t=[0-9]+.[0-9]+\|te=[0-9]+.[0-9]+(\|opt-out=True)?\|c=[0-9A-Z]+$/;

  try {
    const res = regex.test(sid);
    return res;
  } catch {
    return false;
  }
};
